import { FormField, FormTextField } from "@avamae/formbuilder";
import Button from "components/ui/Button";
import { useField, useFormikContext } from "formik";
import { useEffect, useMemo, useRef, useState } from "react";
import { Worker, Viewer, ScrollMode } from "@react-pdf-viewer/core";
// Plugins
import { defaultLayoutPlugin } from "@react-pdf-viewer/default-layout";

// Import styles
import "@react-pdf-viewer/core/lib/styles/index.css";
import "@react-pdf-viewer/default-layout/lib/styles/index.css";
import Papa from "papaparse";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { selectTimeoutPrompt } from "reducers/auth";

import { Transition } from "react-transition-group";

import closeIcon from "assets/Icon_Btn_06.png";
import { dispatch } from "rxjs/internal/observable/pairs";
import {
  setListPageFilters,
  setRedirectUrl,
  setRedirectUrlDestinationModule,
} from "reducers/listPage";
import { APP_ROUTES, FILTER_KEY, endpoints } from "config";
import { useNavigate } from "react-router-dom";
import { Modal } from "components/modal/Modal";
import { ViewDocumentPopUp } from "components/newFlexTable/ViewDocumentCell";
import { resetNotificationItems, selectNotificationItems } from "reducers/workflowNotifications";
import { customUseOnClickOutside } from "../SearchListFieldRenderer/SearchListRenderer";

type Props = {
  field: FormField<any>;
};

export const FileInformation: React.FC<Props> = ({ field }) => {
  const [{ value }, _, helpers] = useField<any>(field.name as string);
  const { values } = useFormikContext<any>();

  const [fullScreen, setFullScreen] = useState<boolean>(false);
  const [showViewDocumentPopUp, setShowViewDocumentPopUp] = useState<boolean>(false);

  const newPlugin = defaultLayoutPlugin();

  const iframeRef = useRef<any>(null);

  const toggleFullscreen = () => {
    const iframe = iframeRef.current;

    if (iframe) {
      if (iframe.requestFullscreen) {
        iframe.requestFullscreen();
      } else if (iframe.mozRequestFullScreen) {
        iframe.mozRequestFullScreen();
      } else if (iframe.webkitRequestFullscreen) {
        iframe.webkitRequestFullscreen();
      } else if (iframe.msRequestFullscreen) {
        iframe.msRequestFullscreen();
      }
    }
  };

  const fileType = useMemo(() => {
    if (value && value.fileExtension) {
      switch (value.fileExtension) {
        case ".PNG":
        case ".png":
        case ".JPEG":
        case ".jpeg":
        case ".GIF":
        case ".gif":
          return "Image";
        case ".MP4":
        case ".mp4":
          return "Video";
        case ".pdf":
        case ".PDF":
          return "PDF";
        case ".docx":
        case ".DOCX":
        case ".xlsx":
        case ".XLSX":
        case ".pptx":
        case ".PPTX":
          return "Office";
        case ".dwg":
        case ".DWG":
          return "CAD";
        default:
          return null;
      }
    }
  }, [value]);

  const openFileInNewTab = (filePath: string) => {
    const newWindow = window.open(filePath, "_blank", "noopener,noreferrer");
    if (newWindow) newWindow.opener = null;
  };
  const getFileViewer = (fileExtension: string) => {
    switch (fileExtension) {
      case ".PNG":
      case ".png":
      case ".JPG":
      case ".jpg":
      case ".JPEG":
      case ".jpeg":
      case ".GIF":
      case ".gif": {
        return (
          <img
            src={value.filePath}
            // Revoke data uri after image is loaded
            onLoad={() => {
              //   URL.revokeObjectURL(file?.preview);
            }}
            alt="File preview"
            style={{ width: "100%" }}
            className="FileImagePreview"
            onClick={() => {
              openFileInNewTab(value.filePath);
            }}
          />
        );
      }
      case ".MP4":
      case ".mp4": {
        return (
          <video width={"100%"} controls className="video" autoPlay={false}>
            <source src={value.filePath} type="video/mp4" />
          </video>
        );
      }
      case ".pdf":
      case ".PDF": {
        return (
          <div
            style={{
              //border: "1px solid rgba(0, 0, 0, 0.3)",
              height: "750px",
            }}
            onClick={() => openFileInNewTab(value.filePath)}
          >
            <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js">
              <Viewer
                fileUrl={value.filePath}
                plugins={[newPlugin]}
                // scrollMode={ScrollMode.Page}
              />
            </Worker>
          </div>
        );
      }
      case ".docx":
      case ".DOCX":
      case ".xlsx":
      case ".XLSX":
      case ".pptx":
      case ".PPTX": {
        return (
          <div style={{ width: "100%" }}>
            <iframe
              ref={iframeRef}
              title={"PDF-Viewer"}
              src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(
                value.filePath
              )}`}
              frameBorder={0}
              style={{ height: "100vh", width: "100%" }} //width: "90vw"
            ></iframe>
          </div>
        );
      }
      case ".csv":
      case ".CSV": {
        return <CsvDisplay csvFileUrl={value.filePath} />;
      }

      default:
        return <div className="NoPreview">No preview</div>;
    }
  };

  const dispatch = useDispatch();

  const navigate = useNavigate();

  const notificationItems = useSelector(selectNotificationItems);

  const viewAllDocuments = () => {
    if (values && values.id) {
      if (notificationItems) {
        dispatch(resetNotificationItems());
      }
      dispatch(setRedirectUrlDestinationModule("/documents"));
      dispatch(
        setRedirectUrl(
          `${endpoints.documents.list}?bTemporaryFilters=true&WorkflowInstancesID=${values.workflowId}&ProjectsID=${values.projectsId}&bClearSearch=true&bClearFilters=true
          `
        )
      );

      navigate(APP_ROUTES.documents);
    }
  };

  const fileExtensionsForSmallPreview = [
    ".png",
    ".PNG",
    ".JPG",
    ".jpg",
    ".JPEG",
    ".jpeg",
    ".GIF",
    ".gif",
    ".mp4",
    ".MP4",
    ".pdf",
    ".PDF",
  ];

  return (
    <div className="FileInformationContainer">
      <div className="FileInformation">
        <div className="Table">
          <div className="Row">
            <div className="Label">Name: </div>
            <div className="Value">{value && value.filename ? value.filename : "-"}</div>
          </div>

          <div className="Row">
            <div className="Label">Type: </div>
            <div className="Value">{value && value.documentType ? value.documentType : "-"}</div>
          </div>
        </div>
      </div>

      {value && value.fileExtension && value.filePath && fullScreen && (
        <CustomFullScreenFilerViewer
          fileExt={value.fileExtension}
          open={fullScreen}
          closeModal={() => setFullScreen(false)}
          getFileViewer={getFileViewer}
        />
      )}

      <div className="FileInformationLinks">
        <div
          className={`ViewDocument Btn ${
            !(values && values.document && values.document.id) ? "Disabled" : ""
          }`}
          onClick={() => {
            if (values && values.document && values.document.id) {
              setShowViewDocumentPopUp(true);
            }
          }}
        >
          View document
        </div>

        <div
          className={`ViewAllDocuments Btn ${
            !(values && values.id && values.document && values.document.filename) ? "Disabled" : ""
          }`}
          onClick={() => viewAllDocuments()}
        >
          View all documents
        </div>
      </div>

      {showViewDocumentPopUp && values && values.document && values.document.id && (
        <Modal
          open={showViewDocumentPopUp}
          closeModal={() => setShowViewDocumentPopUp(false)}
          title={`View Document`}
          extraClassNames="ViewDocument"
        >
          <ViewDocumentPopUp
            id={values.document.id}
            onDone={() => setShowViewDocumentPopUp(false)}
          />
        </Modal>
      )}
    </div>
  );
};

const CustomFullScreenFilerViewer = ({
  fileExt,
  open,
  closeModal,
  getFileViewer,
}: {
  fileExt: string;
  open: boolean;
  closeModal(): void;
  getFileViewer?: (fileExtension: string) => JSX.Element | null;
}) => {
  const [delayedOpen, setDelayedOpen] = React.useState(false);
  React.useEffect(() => {
    if (open) {
      setTimeout(() => {
        setDelayedOpen(true);
      }, 1);
    }
  }, [open]);
  const timeoutPrompt = useSelector(selectTimeoutPrompt);

  const bind = customUseOnClickOutside(() => (!timeoutPrompt ? closeModal : () => {}), true);

  return (
    <Transition in={delayedOpen} timeout={100} mountOnEnter unmountOnExit>
      {(state) => (
        <div className={"ModalUnderlay FileViewer " + state}>
          <div className={"Modal FullSize FileViewer"} {...bind}>
            <div className="CloseIcon">
              <img src={closeIcon} alt="close file preview" onClick={() => closeModal()} />
            </div>
            {getFileViewer && getFileViewer(fileExt)}
          </div>
        </div>
      )}
    </Transition>
  );
};
const CsvDisplay = ({ csvFileUrl }: { csvFileUrl: string }) => {
  const [csvData, setCsvData] = useState<any[]>([]);
  const [headerData, setHeaderData] = useState<string[]>();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(csvFileUrl);
        const csvFile = await response.text();

        Papa.parse(csvFile, {
          header: true, // Treat the first row as headers
          skipEmptyLines: true, // Skip empty lines
          complete: (result) => {
            const tableRows = result.data;
            const headerColumns = result.meta.fields;
            setCsvData(tableRows);
            setHeaderData(headerColumns);
          },
        });
      } catch (error) {
        console.error("Error fetching or parsing CSV file:", error);
      }
    };

    fetchData();
  }, [csvFileUrl]);

  return (
    <div className="CsvTable">
      <table>
        <thead>
          {
            <tr>
              {headerData?.map((header) => (
                <th key={header}>{header}</th>
              ))}
            </tr>
          }
        </thead>
        <tbody>
          {csvData.map((row, index) => (
            <tr key={index}>
              {Object.values(row).map((cell, cellIndex) => (
                <td key={cellIndex}>{row[headerData?.[cellIndex] as any] ?? "-"}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
